/**
 * @file Helpers for managing client-state state related to campaign code attribution.
 */

import { getCookieVal } from "../browser/cookies";
import { getQueryParam } from "../browser/queryParams";
import { wvusLogger } from "../observability/logging";

/**
 * Retrieves the campaign value from the `campaign` cookie.
 * If the cookie is missing or empty, logs an error and attempts to fall back to the campaign query parameter.
 *
 * @see https://worldvision.jira.com/wiki/spaces/DOC/pages/2936569886/CSSC+Attribution
 *
 * @returns The campaign value as a string.
 */
export function getCampaignVal(): string {
  const campaignCookieVal = getCookieVal("campaign", false, "");

  if (!campaignCookieVal) {
    // Attempt to fall back to the campaign query parameter
    const campaignQueryParam = getQueryParam("campaign");

    let errorMessage = "Campaign cookie is missing or empty.";

    if (campaignQueryParam) {
      errorMessage += ` Falling back to query parameter value: ${campaignQueryParam}`;
    } else {
      errorMessage += " Campaign is also missing from query parameters.";
    }

    // Append wvusCSSC value to the error log
    errorMessage += ` wvusCSSC: ${JSON.stringify(window.wvusCSSC)}`;

    wvusLogger.log(errorMessage, wvusLogger.getLogLevelNames().ERROR);

    // Return the fallback value if available, otherwise empty string
    return campaignQueryParam || "";
  }

  return campaignCookieVal;
}
